import {
    Card,
    DateField,
    DateTimeUtils,
    FormField,
    FormTextField,
    IMessage,
    IMessageDisplayText,
    ISpeedDialActionProps,
    Label,
    List,
    MessageLanguage,
    MessageType,
    Segmented,
    SERVER_DATE_FORMAT,
    SpeedDial,
    StringUtils,
    ValidationUtils,
} from '@localina/core';
import { LanguageIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { InferType } from 'yup';
import { MessageDisplayTextListItem } from '../../../components';
import { useMessageSchema } from '../message-utils';

interface IProps {
    type: MessageType;
    onCreateDisplayText: (language: MessageLanguage) => void;
    onEditDisplayText: (messageDisplayText: IMessageDisplayText & { index: number }) => void;
    isDefaultMessage: boolean;
    setIsDefaultMessage: Dispatch<SetStateAction<boolean>>;
}

export const Message: React.FC<IProps> = (props) => {
    const { t } = useTranslation();

    const messageSchema = yup.object(useMessageSchema(props.type));
    type TFormValues = InferType<typeof messageSchema>;

    const methods = useFormContext<TFormValues>();
    const [from, link, displayTexts] = methods.watch(['from', 'link', 'displayTexts']);

    useEffect(() => {
        if (props.isDefaultMessage) {
            methods.setValue('from', '', { shouldDirty: true, shouldValidate: true });
            methods.setValue('to', '', { shouldDirty: true, shouldValidate: true });
        }
    }, [props.isDefaultMessage]);

    const createActions: ISpeedDialActionProps[] = Object.values(MessageLanguage).map((lng) => ({
        label: t(`core:languages.${lng.toLowerCase()}`),
        icon: <LanguageIcon />,
        disabled: displayTexts?.some((it) => it.language.toUpperCase() === lng),
        onClick: () => props.onCreateDisplayText(lng),
    }));

    const validityOptions = [
        { value: true, label: t('message.validity.permanent') },
        { value: false, label: t('message.validity.limited') },
    ];

    return (
        <>
            {}
            <div className="message-layout">
                <div className="message-layout__main">
                    <Card>
                        {}
                        <FormTextField name="name" label={t('message.fields.name')} required />

                        <div className="message-layout__validity">
                            <Label
                                type="label"
                                value={t('message.fields.validity')}
                                tooltip={t('message.infoText.basicInformation')}
                                extraClassName="message-layout__validity__label"
                            />
                            <Segmented
                                options={validityOptions}
                                value={Boolean(props.isDefaultMessage)}
                                onChange={(value) => props.setIsDefaultMessage(Boolean(value))}
                                fullWidth
                                label={t('message.fields.validity')}
                            />
                        </div>

                        {}
                        <div className={`message-layout__dates${props.isDefaultMessage ? ' is-inactive' : ''}`}>
                            <FormField
                                name="from"
                                label={t('message.fields.from')}
                                disabled={Boolean(props.isDefaultMessage)}
                                accepter={DateField}
                            />
                            <FormField
                                name="to"
                                label={t('message.fields.to')}
                                disabled={Boolean(props.isDefaultMessage)}
                                accepter={DateField}
                                minDate={from ? DateTime.fromFormat(from, SERVER_DATE_FORMAT) : undefined}
                            />
                        </div>

                        {props.type !== MessageType.EMAIL && (
                            <FormTextField
                                name="link"
                                label={t('message.fields.link')}
                                helperText={t('message.helperTexts.link')}
                            />
                        )}
                    </Card>
                </div>

                <div className="message-layout__side">
                    {}
                    <Card title={t('message.languages')} flush>
                        <List>
                            {displayTexts?.map((displayText, i) => (
                                <MessageDisplayTextListItem
                                    key={i}
                                    displayText={displayText}
                                    hasLink={Boolean(link)}
                                    onClick={() => {
                                        props.onEditDisplayText({
                                            ...displayText,
                                            index: i,
                                        });
                                    }}
                                />
                            ))}
                        </List>
                    </Card>
                </div>
            </div>
            <SpeedDial label="create" actions={createActions} />
        </>
    );
};

export const validateMessage = (message: IMessage) => {
    const hasName = message.name !== '';
    if (!hasName) {
        return false;
    }

    const hasValidUrl = !message.link || message.link === '' || StringUtils.isValidUrl(message.link);
    if (!hasValidUrl) {
        return false;
    }

    const { from, to } = message;
    const hasNone = !from && from !== '' && !to && to !== '';
    const hasOnlyFrom = !!from && from !== '' && (!to || to === '');
    const hasOnlyTo = !!to && to !== '' && (!from || from === '');
    const hasBoth = !!from && from !== '' && !!to && to !== '';
    const hasValidDateRange =
        hasNone || hasOnlyFrom || hasOnlyTo || (hasBoth && DateTimeUtils.hasValidDateRange(from, to));
    if (!hasValidDateRange) {
        return false;
    }

    const hasDisplayTexts = message.displayTexts.length !== 0;
    if (!hasDisplayTexts) {
        return false;
    }

    return ValidationUtils.validateObject(message, { optionalFields: ['id', 'link', 'from', 'to'] });
};
